import { CONSTANTS } from "../data";
import { calculateXp } from "../utils/gameCalculations";
import { inventoryReducer } from "./reducers/inventoryReducer";
import { customerReducer } from "./reducers/customerReducer";
import { sessionReducer } from "./reducers/sessionReducer";
import { brewingReducer } from "./reducers/brewingReducer";
import { uiReducer } from "./reducers/uiReducer";
import { progressionReducer } from "./reducers/progressionReducer";
import { tutorialReducer } from "./reducers/tutorialReducer";
import { gameDataReducer } from "./reducers/gameDataReducer";

/**
 * Compares a user action with the current tutorial step's required action
 * to determine if the tutorial should advance.
 * 
 * @param action - The action that was just performed
 * @param tutorialState - Current tutorial state
 * @returns boolean - Whether the action matches the tutorial requirement
 */
function doesActionMatchTutorialStep(action, tutorialState) {
  if (
    !tutorialState ||
    !tutorialState.isTutorialActive ||
    !tutorialState.currentTutorial
  ) {
    return false;
  }

  const tutorialDef = CONSTANTS.TUTORIALS[tutorialState.currentTutorial.id];
  if (!tutorialDef) return false;
  const step = tutorialDef.steps[tutorialState.currentTutorial.step];
  if (!step || !step.action) return false;

  const required = step.action;

  // Match by action type
  switch (required.type) {
    case "selectItem":
      // Match ADD_TO_STATION with correct item
      return (
        action.type === "ADD_TO_STATION" &&
        action.item === required.itemId
      );
    case "brew":
      // Match BREW_SUCCESS with correct ingredients (order-insensitive)
      if (action.type !== "BREW_SUCCESS") return false;
      if (!Array.isArray(required.ingredients) || !Array.isArray(action.consumedItems)) return false;
      const reqSorted = [...required.ingredients].sort().join("_");
      const actSorted = [...action.consumedItems].sort().join("_");
      return reqSorted === actSorted;
    case "serve":
      // Match SERVE_CUSTOMER with correct order
      return (
        action.type === "SERVE_CUSTOMER" &&
        action.itemId === required.orderId
      );
    default:
      return false;
  }
}

/**
 * Advances the tutorial state by one step or completes the tutorial
 * if all steps have been completed.
 * 
 * @param tutorialState - Current tutorial state
 * @returns Updated tutorial state
 */
function getAdvancedTutorialState(tutorialState) {
  if (
    !tutorialState ||
    !tutorialState.isTutorialActive ||
    !tutorialState.currentTutorial
  ) {
    return tutorialState;
  }
  const tutorialDef = CONSTANTS.TUTORIALS[tutorialState.currentTutorial.id];
  if (!tutorialDef) return tutorialState;
  const currentStep = tutorialState.currentTutorial.step;
  const nextStep = currentStep + 1;

  if (nextStep >= tutorialDef.steps.length) {
    // Tutorial complete
    return {
      ...tutorialState,
      isTutorialActive: false,
      currentTutorial: null,
      completedTutorials: [
        ...(tutorialState.completedTutorials || []),
        tutorialState.currentTutorial.id,
      ],
    };
  } else {
    // Advance to next step
    return {
      ...tutorialState,
      currentTutorial: {
        ...tutorialState.currentTutorial,
        step: nextStep,
      },
    };
  }
}

/**
 * Initial game state with all default values.
 * This represents a fresh game with starting resources and unlocked content.
 */
export const initialGameState = {
  // Core game state
  coins: CONSTANTS.STARTING_COINS,
  xp: 0,
  level: 1,
  prestigeLevel: 0,
  reputation: CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION,
  
  // Inventory
  inventory: { ...CONSTANTS.STARTING_INVENTORY },
  inventoryActiveTab: 'ingredients',
  inventoryPanelOpen: true,
  customerQueueOpen: true,
  
  // Game mode and session
  sessionMode: { ...CONSTANTS.DEFAULT_SESSION_MODE },
  seededSession: false,
  customSeed: "",
  
  // Brewing - Updated to use grid coordinates
  brewStations: [
    { slots: [null, null], quantity: 1, gridPosition: { tileX: -1, tileY: -1 } },
  ],
  lastQualityGrade: null,
  
  // Customers
  customers: [],
  customerIdCounter: 1,
  lastCustomerSpawn: 0,
  
  // Recipes and discoveries
  discoveredRecipes: [],
  lastDiscovery: null,
  aiGeneratedRecipes: {},
  
  // Events and special states
  affogatoEvent: { active: false, endTime: 0, lastTrigger: 0 },
  dailyBlend: {
    trio: [],
    progress: [],
    completed: false,
    buffActive: false,
    buffExpiry: 0,
    lastUpdatedDay: -1,
  },
  criticVisit: {
    active: false,
    endTime: 0,
    lastTrigger: 0,
    currentOrder: 0,
    completedOrders: [],
  },
  nitroUnlocked: false,
  
  // Legendary systems
  legendaryUnlocks: {
    temperatureControl: false,
    environmentalModifiers: true,
    qualityGrades: false,
  },
  phoenixBlendSequence: {
    active: false,
    progress: [],
    lastServed: 0,
  },
  auroraConditions: {
    active: false,
    icedCount: 0,
    startTime: 0,
  },
  mastersHarmonyConditions: {
    active: false,
    perfectCount: 0,
    goodCount: 0,
    startTime: 0,
  },
  
  // Advanced mechanics
  temperatureControl: {
    enabled: false,
    currentTemp: CONSTANTS.TEMPERATURE_CONTROL.defaultTemp,
  },
  environmentalState: {
    enabled: true,
    currentTimeOfDay: "MORNING",
    humidity: 50,
    gameStartTime: Date.now(),
    dayIndex: 0,
    dayProgress: 0,
  },
  qualityGrades: {
    enabled: false,
  },
  
  // Seasonal and rotating content
  activeSeason: null,
  
  // Quests and challenges
  quests: [],
  masterChefChallenge: { 
    active: false,
    id: null,
    startTime: 0,
    progress: 0,
    goal: 0,
    duration: 0
  },
  
  // UI and tutorial state
  screen: "intro", // intro, menu, game, results
  showModal: null, // shop, challenges, recipeBook, settings, resetConfirm
  hudHint: null,
  levelUpNotification: null,
  discoveryNotification: null,
  latteArtGame: { active: false },
  trendTip: { message: "", active: false },
  
  // Tutorial state
  tutorialState: {
    isTutorialActive: false,
    currentTutorial: null,
    completedTutorials: [],
    skippedTutorials: [],
  },
  
  // Settings
  settings: { ...CONSTANTS.DEFAULT_SETTINGS },
  
  // Rush mode specific
  lastRushScore: {
    score: 0,
    served: 0,
    tips: 0,
    combos: 0,
    legendaries: 0,
    categories: 0,
  },
  rushModeState: {
    originalInventory: null,
    originalDiscoveredRecipes: null,
  },
  
  // AI and dynamic systems
  aiBarista: {
    active: false,
    level: 0, // 0 = not purchased
    speed: 0,
    efficiency: 0,
  },
  aiTipState: {
    failures: 0,
    timeouts: 0,
    lastFailureTime: 0,
  },
  customerArchetypes: {},
  trendPrediction: {
    lastUpdate: 0,
    trendingItems: [],
  },
  
  // Latte art buff
  latteArtBuff: {
    active: false,
    endTime: 0,
  },
  
  // Loading states
  isLoading: false,

  // Equipment
  equipmentLevels: {
    grinder: 1,
    espresso_machine: 1,
  },
  decorations: {},
  lastCreationTimestamps: {},
  qualityMultipliers: {},
  trendingDrink: { id: null, endTime: 0 },
};

/**
 * Main game state reducer that routes actions to appropriate domain-specific reducers.
 * This follows a modular reducer pattern where each domain (inventory, customers, etc.)
 * has its own reducer function.
 * 
 * The reducer also handles tutorial advancement by checking if actions match
 * the current tutorial step requirements.
 * 
 * @param state - Current game state
 * @param action - Action to be processed
 * @returns Updated game state
 */
export function gameReducer(state, action) {
  let newState = state;

  // Route actions to appropriate domain reducers
  // Each reducer returns the original state if it doesn't handle the action
  newState = inventoryReducer(newState, action);
  if (newState !== state) {
    // Action was handled by inventory reducer
  } else {
    newState = customerReducer(newState, action);
    if (newState !== state) {
      // Action was handled by customer reducer
    } else {
      newState = sessionReducer(newState, action);
      if (newState !== state) {
        // Action was handled by session reducer
      } else {
        newState = brewingReducer(newState, action);
        if (newState !== state) {
          // Action was handled by brewing reducer
        } else {
          newState = uiReducer(newState, action);
          if (newState !== state) {
            // Action was handled by UI reducer
          } else {
            newState = progressionReducer(newState, action);
            if (newState !== state) {
              // Action was handled by progression reducer
            } else {
              newState = tutorialReducer(newState, action);
              if (newState !== state) {
                // Action was handled by tutorial reducer
              } else {
                newState = gameDataReducer(newState, action);
                // If no reducer handled it, gameDataReducer will return the original state
              }
            }
          }
        }
      }
    }
  }

  // Tutorial advancement check
  // After every action, check if tutorial is active and if this action matches the current step
  if (
    newState.tutorialState &&
    newState.tutorialState.isTutorialActive &&
    newState.tutorialState.currentTutorial
  ) {
    if (doesActionMatchTutorialStep(action, newState.tutorialState)) {
      // Advance tutorial step
      newState = {
        ...newState,
        tutorialState: getAdvancedTutorialState(newState.tutorialState),
      };
    }
  }

  return newState;
}
